import { useMemo } from 'react';
import { CollectibleObject, UserItemInstance } from '../types';
import { createEffectVersion } from '../utils/itemEffects';

interface UseEnhancedObjectsParams {
  baseObjects: CollectibleObject[];
  userInstances: UserItemInstance[];
}

/**
 * Hook qui génère les objets affichables basés sur les instances utilisateur
 * Chaque instance peut avoir des effets appliqués différents
 */
export const useEnhancedObjects = ({ baseObjects, userInstances }: UseEnhancedObjectsParams) => {

  const allObjectsWithEffects = useMemo(() => {
    // Retourner les objets de base + générer des versions avec effets pour les instances
    // Les baseObjects sont toujours disponibles pour la liste complète
    // Mais on affiche seulement ce que l'utilisateur possède via userInstances

    // Pour l'affichage dans WorldScreen/CollectionScreen, on a besoin des baseObjects
    // pour afficher les sprites et les noms
    return baseObjects;

    // Note: Les effets seront appliqués au moment du rendu via ItemEffectsRenderer
    // en lisant userInstances.appliedEffects
  }, [baseObjects]);

  // Compter les instances par element ID pour backward compatibility
  const instanceCounts = useMemo(() => {
    const counts = new Map<number, number>();
    userInstances
      .filter(inst => !inst.archived)
      .forEach(inst => {
        const current = counts.get(inst.baseElementId) || 0;
        counts.set(inst.baseElementId, current + 1);
      });
    return counts;
  }, [userInstances]);

  return {
    allObjects: allObjectsWithEffects,
    baseObjects,
    userInstances,
    instanceCounts,
    hasTransformedObjects: userInstances.some(inst => inst.appliedEffects && inst.appliedEffects.length > 0)
  };
};

export default useEnhancedObjects;